import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { z } from "zod/v4";
import * as rpc from "../../../common/jsonRpcRoutes";
import { isAbortedRequest, isUnconfirmedOutcome, useRpcCaller, type RpcCaller } from "../context/WidgetRequestContext";
import type {
  Availability,
  BookingCreateRequest,
  BookingCreateResponse,
  BookingOptions,
  BookingSubmitRequest,
  BookingSubmitResponse,
  BookingType,
  CountryConfig,
  VenueConfig,
  Rewards,
} from "../../../common/jsonRpcTypes";
import { customDetailsParser } from "../../../common/jsonRpcTypes";
import { resolveCountryCode } from "../../../utils/countryLocaleMap";
import { getDurationOptions, getTimeOptions, type DurationOption, type TimeOption } from "../../utils/bookingRules";
import { useWidgetLocale } from "./useWidgetLocale";

// ─── State Shape ───

export interface CoreBookingDetails {
  type: string | null;
  num_people: number | null;
  date: string | null;
  time: string | null;
  duration: number | null;
  venue_id?: string | null;
}

/** Zod schema for the authenticated user returned by the /createBooking API response. */
const pendingBookingUserParser = z.object({
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  phone: z.string().nullish(),
  dob: z.string().nullish(),
  // Reward points balance for a pre-authenticated guest, used by the rewards
  // banner. `.nullish()` rather than `.optional()` because `z.coerce` would turn
  // a null into 0 and show a balance of zero instead of omitting it; null is
  // normalised to undefined so callers get `number | undefined`.
  points_balance: z.coerce
    .number()
    .nullish()
    .transform((value) => value ?? undefined),
  custom_details: customDetailsParser,
});

/**
 * Minimal Zod schema for validating the booking type object from the API response.
 * Only includes the six fields consumed by ResolvedBookingType, avoiding the full
 * bookingTypeParser which contains sub-schemas (booking_rules, customer_preorder_settings,
 * etc.) that could cause safeParse to reject an otherwise usable type object.
 */
const resolvedBookingTypeParser = z.object({
  id: z.string(),
  name: z.string(),
  value: z.number().optional(),
  guestlist: z.boolean().optional(),
  private_hire: z.boolean().optional(),
  use_standing_capacity: z.boolean().optional(),
});

/** User details returned by the /createBooking response when the guest is already authenticated. */
export type PendingBookingUser = z.infer<typeof pendingBookingUserParser>;

export interface BookingWizardState {
  loading: boolean;
  error: string | null;
  submitting: boolean;
  /** Error from /submitBooking network failures (separate from createBooking errors). */
  submitError: string | null;
  /** /submitBooking timed out, so whether the booking was made is unknown. */
  submitUnconfirmed: boolean;
  /** A payment finalise timed out, so whether the payment went through is unknown. */
  paymentUnconfirmed: boolean;
  stage:
    | "initial"
    | "details"
    | "availability"
    | "preorders"
    | "payment"
    | "deposit"
    | "complete"
    | "complete-redirect";
  pendingBookingId: string | null;
  accessToken: string | null;
  venueConfig: VenueConfig | null;
  countryConfig: CountryConfig | null;
  bookingOptions: BookingOptions | null;
  availability: Availability | null;
  offers: Offer[];
  packages: Package[];
  coreBookingDetails: CoreBookingDetails;
  timeOptions: TimeOption[];
  durationOptions: DurationOption[];
  canAutoconfirm: boolean;
  /**
   * Whether the venue hides the allergens and dietary controls. Collins sends
   * this as `showDietaryRequirements`, but that field is populated from the
   * venue's `hide_dietary_requirements` setting, so it is inverted here once,
   * at the boundary, and carried under its true meaning from then on.
   */
  hideDietaryRequirements: boolean;
  errors: Record<string, string[]> | null;
  createBookingFailed: boolean;
  /** Payment processor for the pending booking (e.g. "stripe_connect", "freedom_pay"). */
  paymentProcessor: string | null;
  /** Response from /submitBooking, used to populate the confirmation page. */
  submitResult: BookingSubmitResponse | null;
  /** Authenticated user returned by /createBooking when the guest is already logged in. */
  pendingBookingUser: PendingBookingUser | null;
  /** Rewards data returned by /createBooking (points earned for this booking). */
  rewards: Rewards | null;
  isEnquiry: boolean;
  /** Affiliate ID passed via URL query parameter. */
  affiliateId: string | null;
  /** Locale passed via URL query parameter. */
  locale: string | null;
  /** Affiliate data returned by the DMN /book response. */
  affiliate: AffiliateData | null;
  /** Booking type details resolved from the latest /createBooking API response. */
  resolvedBookingType: ResolvedBookingType | null;
}

/** Booking type details extracted from the /createBooking API response booking object. */
export interface ResolvedBookingType {
  id: string;
  name: string;
  value: number;
  guestlist: boolean;
  private_hire: boolean;
  use_standing_capacity: boolean;
}

export interface AffiliateData {
  _id: string;
  name: string;
  data_permission_text?: string;
  hide_login_section?: boolean;
  photo?: string;
}

interface Offer {
  _id: string;
  title: string;
  description?: string;
}

interface Package {
  id: string;
  label: string;
}

// ─── Reducer Actions ───

type BookingWizardAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_BOOKING_DATA"; payload: BookingCreateResponse }
  | { type: "SUPERSEDED_BOOKING_DATA"; payload: BookingCreateResponse }
  | { type: "SET_ERROR"; payload: string }
  | { type: "CLEAR_ERROR" }
  | { type: "CLEAR_VALIDATION_ERRORS" }
  | { type: "SET_SUBMIT_ERROR"; payload: string }
  | { type: "SET_SUBMIT_UNCONFIRMED" }
  | { type: "SET_PAYMENT_UNCONFIRMED" }
  | { type: "CLEAR_SUBMIT_ERROR" }
  | { type: "SET_SUBMITTING"; payload: boolean }
  | { type: "SET_SUBMIT_RESULT"; payload: BookingSubmitResponse }
  | { type: "UPDATE_CORE_DETAILS"; payload: Partial<CoreBookingDetails> }
  | { type: "SET_STAGE"; payload: BookingWizardState["stage"] }
  | { type: "SET_TIME_OPTIONS"; payload: TimeOption[] }
  | { type: "SET_DURATION_OPTIONS"; payload: DurationOption[] }
  | { type: "SET_PENDING_BOOKING_USER"; payload: PendingBookingUser }
  | { type: "CLEAR_PENDING_BOOKING_USER" }
  | { type: "SET_IS_ENQUIRY"; payload: boolean }
  | { type: "SET_AFFILIATE"; payload: AffiliateData }
  | { type: "RESET_FOR_VENUE_CHANGE" };

// ─── Initial State ───

const initialState: BookingWizardState = {
  loading: true,
  error: null,
  submitting: false,
  submitError: null,
  submitUnconfirmed: false,
  paymentUnconfirmed: false,
  stage: "initial",
  pendingBookingId: null,
  accessToken: null,
  venueConfig: null,
  countryConfig: null,
  bookingOptions: null,
  availability: null,
  offers: [],
  packages: [],
  coreBookingDetails: {
    type: null,
    num_people: null,
    date: null,
    time: null,
    duration: null,
    venue_id: null,
  },
  timeOptions: [],
  durationOptions: [],
  canAutoconfirm: false,
  hideDietaryRequirements: false,
  errors: null,
  createBookingFailed: false,
  paymentProcessor: null,
  submitResult: null,
  pendingBookingUser: null,
  rewards: null,
  isEnquiry: false,
  affiliateId: null,
  locale: null,
  affiliate: null,
  resolvedBookingType: null,
};

// ─── Helpers for reducer ───

function buildBookingDataState(state: BookingWizardState, response: BookingCreateResponse): BookingWizardState {
  const pendingBookingId = response.booking?._id ?? state.pendingBookingId;
  const accessToken = response.accessToken ?? state.accessToken;
  const booking = response.booking;

  const rawTypeFromResponse = booking?.type?.id ?? booking?.type;
  const responseTypeId = typeof rawTypeFromResponse === "string" ? rawTypeFromResponse : null;

  const typeExistsInVenueConfig =
    responseTypeId != null && response.venueConfig?.bookingTypes?.some((bt) => bt.id === responseTypeId);

  const coreBookingDetails: CoreBookingDetails = {
    type: typeExistsInVenueConfig ? responseTypeId : (state.coreBookingDetails.type ?? responseTypeId),
    num_people: booking?.num_people ?? state.coreBookingDetails.num_people,
    date: booking?.date ? booking.date.split("T")[0] : state.coreBookingDetails.date,
    time: booking?.time ?? state.coreBookingDetails.time,
    duration: booking?.duration ?? state.coreBookingDetails.duration,
    venue_id: booking?.venue_id ?? state.coreBookingDetails.venue_id,
  };

  const resolvedBookingType = extractResolvedBookingType(booking?.type, response.venueConfig, coreBookingDetails.type);

  const errors = response.errors?.errors ?? null;

  return {
    ...state,
    loading: false,
    error: null,
    createBookingFailed: false,
    pendingBookingId,
    accessToken,
    venueConfig: response.venueConfig,
    countryConfig: response.countryConfig,
    bookingOptions: response.bookingOptions ?? state.bookingOptions,
    availability: response.availability ?? null,
    offers: response.offers ?? [],
    packages: response.packages ?? [],
    coreBookingDetails,
    canAutoconfirm: response.canAutoconfirm ?? false,
    // See BookingWizardState.hideDietaryRequirements: the Collins field name is
    // the opposite of what it carries.
    hideDietaryRequirements: Boolean(response.showDietaryRequirements),
    errors,
    paymentProcessor: response.paymentProcessorType ?? state.paymentProcessor,
    pendingBookingUser: extractPendingBookingUser(response.user) ?? state.pendingBookingUser,
    rewards: response.rewards ?? null,
    resolvedBookingType: resolvedBookingType ?? state.resolvedBookingType,
  };
}

// ─── Reducer ───

/**
 * Apply what little of a `/createBooking` response the guest has navigated past
 * still holds true.
 *
 * Its outcome belongs to a step they have left, so none of it may be shown:
 * the validation errors would put the warning on the step they moved to, the
 * availability would either render its feedback there or auto-advance them off
 * it, and the booking details would overwrite choices they have since changed.
 *
 * The one part still worth keeping is the access token, which describes the
 * pending booking rather than the superseded request — but only when the
 * response is for the pending booking the wizard is still working on. A
 * response for an abandoned one (the guest picked another venue, so the journey
 * restarted on a fresh pending booking) contributes nothing at all.
 *
 * `loading` is deliberately untouched: it is shared by every booking request,
 * so only the caller knows whether this was the last one still running (see
 * `updateBooking`).
 */
function applySupersededBookingData(state: BookingWizardState, response: BookingCreateResponse): BookingWizardState {
  const isSamePendingBooking = !!state.pendingBookingId && response.booking?._id === state.pendingBookingId;
  if (!isSamePendingBooking) return state;

  return { ...state, accessToken: response.accessToken ?? state.accessToken };
}

function bookingWizardReducer(state: BookingWizardState, action: BookingWizardAction): BookingWizardState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload, error: action.payload ? null : state.error };

    case "SET_BOOKING_DATA":
      return buildBookingDataState(state, action.payload);

    case "SUPERSEDED_BOOKING_DATA":
      return applySupersededBookingData(state, action.payload);

    case "SET_ERROR":
      return {
        ...state,
        loading: false,
        submitting: false,
        error: action.payload,
        createBookingFailed: true,
      };

    case "CLEAR_ERROR":
      return { ...state, error: null, createBookingFailed: false };

    case "CLEAR_VALIDATION_ERRORS":
      return { ...state, errors: null };

    case "SET_SUBMIT_ERROR":
      return {
        ...state,
        submitting: false,
        submitError: action.payload,
      };

    case "SET_SUBMIT_UNCONFIRMED":
      return { ...state, submitting: false, submitError: null, submitUnconfirmed: true };

    case "SET_PAYMENT_UNCONFIRMED":
      return { ...state, paymentUnconfirmed: true };

    case "CLEAR_SUBMIT_ERROR":
      return { ...state, submitError: null };

    case "SET_SUBMITTING":
      return {
        ...state,
        submitting: action.payload,
        submitError: action.payload ? null : state.submitError,
      };

    case "SET_SUBMIT_RESULT":
      // Stage transition is handled by the caller after awaiting submitBooking,
      // so we no longer force stage: "complete" here. This allows callers to
      // choose between "complete" and "complete-redirect" as appropriate.
      return {
        ...state,
        submitting: false,
        error: null,
        submitResult: action.payload,
      };

    case "UPDATE_CORE_DETAILS":
      return {
        ...state,
        coreBookingDetails: {
          ...state.coreBookingDetails,
          ...action.payload,
        },
      };

    case "SET_STAGE":
      return {
        ...state,
        stage: action.payload,
        availability: action.payload === "initial" ? null : state.availability,
        isEnquiry: action.payload === "initial" ? false : state.isEnquiry,
      };
    case "SET_IS_ENQUIRY":
      return { ...state, isEnquiry: action.payload };

    case "SET_TIME_OPTIONS":
      return { ...state, timeOptions: action.payload };

    case "SET_DURATION_OPTIONS":
      return { ...state, durationOptions: action.payload };

    case "SET_PENDING_BOOKING_USER":
      return { ...state, pendingBookingUser: action.payload };

    case "CLEAR_PENDING_BOOKING_USER":
      return { ...state, pendingBookingUser: null };

    case "SET_AFFILIATE":
      return { ...state, affiliate: action.payload };

    case "RESET_FOR_VENUE_CHANGE":
      return {
        ...initialState,
        stage: state.stage,
        isEnquiry: state.isEnquiry,
        affiliateId: state.affiliateId,
        locale: state.locale,
        affiliate: state.affiliate,
      };

    default:
      return state;
  }
}

// ─── Helpers ───

/**
 * Parse a typed PendingBookingUser from the raw `/createBooking` response `user` field.
 * The API returns `false` when no authenticated user is present.
 * Uses Zod validation instead of manual type casting.
 */
function extractPendingBookingUser(raw: unknown): PendingBookingUser | null {
  if (!raw || raw === false) return null;
  const parsed = pendingBookingUserParser.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Find the selected booking type object from venue config. */
function findSelectedBookingType(venueConfig: VenueConfig | null, typeId: string | null): BookingType | undefined {
  if (!venueConfig || !typeId) return undefined;
  return venueConfig.bookingTypes.find((bt) => bt.id === typeId);
}

/**
 * Extract resolved booking type details from the /createBooking API response.
 * Prefers the venueConfig lookup (authoritative), falling back to the booking
 * object's type when the lookup fails (cross-sell scenario where the type ID
 * belongs to the original venue rather than the newly selected one).
 */
function extractResolvedBookingType(
  bookingType: unknown,
  venueConfig: VenueConfig | undefined,
  typeId: string | null,
): ResolvedBookingType | null {
  // First try the venueConfig lookup — this is the authoritative source
  if (venueConfig && typeId) {
    const fromConfig = venueConfig.bookingTypes.find((bt) => bt.id === typeId);
    if (fromConfig) {
      return {
        id: fromConfig.id,
        name: fromConfig.name,
        value: fromConfig.value ?? 0,
        guestlist: fromConfig.guestlist ?? false,
        private_hire: fromConfig.private_hire ?? false,
        use_standing_capacity: fromConfig.use_standing_capacity ?? false,
      };
    }
  }

  // Fall back to the booking response's type object (used in cross-sell scenarios).
  // Validated with Zod rather than manual typeof checks, following the same pattern
  // as extractPendingBookingUser.
  const parsed = resolvedBookingTypeParser.safeParse(bookingType);
  if (parsed.success && parsed.data.id && parsed.data.name) {
    return {
      id: parsed.data.id,
      name: parsed.data.name,
      value: parsed.data.value ?? 0,
      guestlist: parsed.data.guestlist ?? false,
      private_hire: parsed.data.private_hire ?? false,
      use_standing_capacity: parsed.data.use_standing_capacity ?? false,
    };
  }

  return null;
}

interface FetchAndCreateParams {
  venueId: string;
  source: string;
  affiliateId?: string;
  offer?: string;
  locale?: string;
  additionalTypes?: string;
  venueGroup?: string;
}

function buildPendingBookingRpcParams(params: FetchAndCreateParams) {
  return {
    venue_id: params.venueId,
    source: params.source,
    ...(params.affiliateId ? { affiliate_id: params.affiliateId } : {}),
    ...(params.offer ? { offer: params.offer } : {}),
    ...(params.locale ? { locale: params.locale } : {}),
  };
}

function buildCreateBookingRpcParams(
  params: FetchAndCreateParams,
  pendingBookingId: string | null,
  initialBookingDetails?: {
    type?: string;
    num_people?: number;
    date?: string;
    time?: string;
    duration?: number;
  },
) {
  return {
    venue_id: params.venueId,
    source: params.source,
    ...(pendingBookingId ? { pending_booking_id: pendingBookingId } : {}),
    ...(params.affiliateId ? { affiliate_id: params.affiliateId } : {}),
    ...(params.offer ? { offer: params.offer } : {}),
    ...(params.locale ? { locale: params.locale } : {}),
    ...(params.additionalTypes ? { additionalTypes: params.additionalTypes } : {}),
    ...(params.venueGroup ? { venue_group: params.venueGroup } : {}),
    ...(initialBookingDetails?.type ? { type: initialBookingDetails.type } : {}),
    ...(initialBookingDetails?.num_people ? { num_people: initialBookingDetails.num_people } : {}),
    ...(initialBookingDetails?.date ? { date: initialBookingDetails.date } : {}),
    ...(initialBookingDetails?.time ? { time: initialBookingDetails.time } : {}),
    ...(initialBookingDetails?.duration ? { duration: initialBookingDetails.duration } : {}),
  };
}

async function fetchPendingAndCreateBooking(
  params: FetchAndCreateParams,
  dispatch: React.Dispatch<BookingWizardAction>,
  cancelled?: { current: boolean },
  initialBookingDetails?: {
    type?: string;
    num_people?: number;
    date?: string;
    time?: string;
    duration?: number;
  },
  call: RpcCaller = rpc.call,
): Promise<void> {
  let pendingBookingId: string | null = null;
  let affiliate: AffiliateData | null = null;

  try {
    const result = await call("/fetchPendingBookingId", buildPendingBookingRpcParams(params));
    pendingBookingId = result.pending_booking_id;
    affiliate = result.affiliate ?? null;
    const user = extractPendingBookingUser(result.user);
    if (cancelled?.current) return;
    if (affiliate) {
      dispatch({ type: "SET_AFFILIATE", payload: affiliate });
    }
    if (user) {
      dispatch({ type: "SET_PENDING_BOOKING_USER", payload: user });
    }
  } catch (err) {
    if (isAbortedRequest(err)) return;
    console.warn("Failed to fetch pending_booking_id, continuing without it", err);
  }

  if (cancelled?.current) return;

  const data = await call(
    "/createBooking",
    buildCreateBookingRpcParams(params, pendingBookingId, initialBookingDetails),
  );
  if (cancelled?.current) return;
  dispatch({ type: "SET_BOOKING_DATA", payload: data });
}

/**
 * The identifiers that stay constant for the lifetime of a booking wizard and
 * accompany every `/createBooking` call.
 */
interface PendingBookingContext {
  venueId: string;
  source: string;
  pendingBookingId: string | null;
  affiliateId?: string;
  offer?: string;
  locale?: string;
  additionalTypes?: string;
  venueGroup?: string;
}

/**
 * Build the `/createBooking` payload used to update an existing pending booking.
 *
 * Shared by `updateBooking` and `persistOfferToPendingBooking` so the two cannot
 * drift apart.
 *
 * `offerOverride` is the guest's current choice and takes precedence over the
 * `?offer=` param held in the context: every update has to carry the latest
 * choice, or changing booking details after declining or swapping an offer
 * would restore the one from the URL.
 *
 * An empty string is a meaningful value throughout — Collins unsets the stored
 * offer when the field is present but blank — so it is distinguished from
 * `undefined` ("leave the stored offer alone") rather than treated as falsy.
 */
function buildUpdateBookingRequest(
  context: PendingBookingContext,
  details: CoreBookingDetails,
  offerOverride?: string,
): BookingCreateRequest {
  const offer = offerOverride ?? context.offer;

  return {
    venue_id: details.venue_id ?? context.venueId,
    source: context.source,
    ...(context.pendingBookingId ? { pending_booking_id: context.pendingBookingId } : {}),
    ...(details.type ? { type: details.type } : {}),
    ...(details.num_people ? { num_people: details.num_people } : {}),
    ...(details.date ? { date: details.date } : {}),
    ...(details.time ? { time: details.time } : {}),
    // Send duration to API: -1 ("until close") is sent as omitted/null
    ...(details.duration && details.duration !== -1 ? { duration: details.duration } : {}),
    ...(context.affiliateId ? { affiliate_id: context.affiliateId } : {}),
    ...(offer !== undefined ? { offer } : {}),
    ...(context.locale ? { locale: context.locale } : {}),
    ...(context.additionalTypes ? { additionalTypes: context.additionalTypes } : {}),
    ...(context.venueGroup ? { venue_group: context.venueGroup } : {}),
  };
}

// ─── Hook ───

export interface UseBookingCreateOptions {
  affiliateId?: string;
  /**
   * The offer from the `?offer=` query param, used to seed the pending booking.
   *
   * This must stay stable for the lifetime of the journey: the initial-load
   * effect is keyed on it, so changing it starts a fresh
   * `fetchPendingAndCreateBooking` — a new `pending_booking_id` seeded from the
   * URL, discarding the booking the guest has been building. The guest's own
   * choice is therefore passed per call to `updateBooking` instead.
   */
  offer?: string;
  locale?: string;
  additionalTypes?: string;
  venueGroup?: string;
  // Initial booking details from URL params
  type?: string;
  num_people?: number;
  date?: string;
  time?: string;
  duration?: number;
}

export function useBookingCreate(
  venueId: string,
  source: string,
  options?: UseBookingCreateOptions,
): {
  state: BookingWizardState;
  /**
   * Updates the pending booking with changed core details.
   *
   * `offerOverride` carries the guest's current offer choice, which the caller
   * owns: an empty string clears a stored offer, `undefined` falls back to the
   * `?offer=` param. It is an argument rather than a hook option because the
   * initial-load effect keys on the option (see {@link UseBookingCreateOptions.offer}).
   */
  updateBooking: (details: Partial<CoreBookingDetails>, offerOverride?: string) => Promise<void>;
  /**
   * Writes the guest's offer choice to the pending booking so it survives a
   * payment redirect. Pass `null` to clear a previously stored offer.
   */
  persistOfferToPendingBooking: (offerId: string | null) => Promise<void>;
  updateCoreDetails: (details: Partial<CoreBookingDetails>) => void;
  submitBooking: (fullDetails: BookingSubmitRequest) => Promise<void>;
  retryCreateBooking: () => Promise<void>;
  clearSubmitError: () => void;
  clearValidationErrors: () => void;
  clearError: () => void;
  setStage: (stage: BookingWizardState["stage"]) => void;
  setIsEnquiry: (isEnquiry: boolean) => void;
  /** Marks the payment outcome as unknown — sticky for the rest of the session. */
  setPaymentUnconfirmed: () => void;
  /** Clears the authenticated user linked to the pending booking (used on logout). */
  clearPendingBookingUser: () => void;
  resetForVenueChange: () => void;
} {
  const affiliateId = options?.affiliateId;
  const offer = options?.offer;
  const locale = options?.locale;
  const additionalTypes = options?.additionalTypes;
  const venueGroup = options?.venueGroup;
  const initialType = options?.type;
  const initialNumPeople = options?.num_people;
  const initialDate = options?.date;
  const initialTime = options?.time;
  const initialDuration = options?.duration;

  const [state, dispatch] = useReducer(bookingWizardReducer, initialState);
  const { venueConfig, countryConfig, coreBookingDetails, durationOptions } = state;

  /**
   * Which `/createBooking` update the wizard is currently waiting on.
   *
   * Held in a ref rather than in the reducer: it is request identity, it must
   * not trigger a render, and it has to be readable at call time — a value read
   * from a render closure could be stale and wrongly supersede a fresh request.
   */
  const updateGenerationRef = useRef(0);
  /**
   * How many booking requests are still running.
   *
   * `loading` is shared by all of them, so a superseded response can only
   * settle it once it is the last one out; the generation says whose data is
   * current, not whether anything else is still in flight.
   */
  const activeRequestsRef = useRef(0);
  const call = useRpcCaller();

  // Locale hint for the time/end-time dropdown labels, so they read the same way
  // as the times shown elsewhere in the journey (24-hour for GB, 12-hour for US/AU).
  const countryCode = resolveCountryCode(countryConfig?.country_code);
  const timeLocale = useWidgetLocale(countryConfig?.country_code);

  const {
    type: selectedType,
    date: selectedDate,
    time: selectedTime,
    num_people: selectedNumPeople,
  } = coreBookingDetails;

  // Track previous selectedTime so we only reset duration on genuine time changes,
  // not on initial population from the API response.
  const prevTimeRef = useRef(selectedTime);
  const [timeOptionsTick, setTimeOptionsTick] = useState(0);
  const lastTimeOptionsRef = useRef<string | null>(null);
  const timeOptionsInputsRef = useRef<{
    venueConfig: VenueConfig;
    selectedType: string;
    selectedDate: string;
    source: string;
    countryCode: string;
    timeLocale: string | undefined;
  } | null>(null);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setTimeOptionsTick((tick) => tick + 1);
    }, 30_000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setTimeOptionsTick((tick) => tick + 1);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // ── Initial load: fetch pending_booking_id then call /createBooking ──
  useEffect(() => {
    if (!venueId) return;

    const cancelledRef = { current: false };

    dispatch({ type: "SET_LOADING", payload: true });

    // Abandoning this journey (a venue change) has to release its hold on
    // `loading` at once: its response will be discarded, so the stage the guest
    // is actually on must not wait for the network call to come back before its
    // own requests can settle the overlay. Idempotent, because the cleanup and
    // the request's own completion race to run it.
    activeRequestsRef.current += 1;
    let released = false;
    const releaseRequest = () => {
      if (released) return;
      released = true;
      activeRequestsRef.current -= 1;
    };

    (async () => {
      try {
        await fetchPendingAndCreateBooking(
          { venueId, source, affiliateId, offer, locale, additionalTypes, venueGroup },
          dispatch,
          cancelledRef,
          {
            type: initialType,
            num_people: initialNumPeople,
            date: initialDate,
            time: initialTime,
            duration: initialDuration,
          },
          call,
        );
      } catch (err) {
        if (!cancelledRef.current && !isAbortedRequest(err)) {
          const message = err instanceof Error ? err.message : "An unknown error has occurred";
          dispatch({ type: "SET_ERROR", payload: message });
        }
      } finally {
        releaseRequest();
      }
    })();

    return () => {
      cancelledRef.current = true;
      releaseRequest();
    };
  }, [
    call,
    venueId,
    source,
    affiliateId,
    offer,
    locale,
    additionalTypes,
    venueGroup,
    initialType,
    initialNumPeople,
    initialDate,
    initialTime,
    initialDuration,
  ]);

  // ── Reactive computation: recompute timeOptions when type or date changes ──
  // biome-ignore lint/correctness/useExhaustiveDependencies: the tick intentionally triggers recomputation as the clock advances
  useEffect(() => {
    if (!venueConfig || !selectedType || !selectedDate) {
      lastTimeOptionsRef.current = null;
      timeOptionsInputsRef.current = null;
      dispatch({ type: "SET_TIME_OPTIONS", payload: [] });
      return;
    }

    const previousInputs = timeOptionsInputsRef.current;
    if (
      !previousInputs ||
      previousInputs.venueConfig !== venueConfig ||
      previousInputs.selectedType !== selectedType ||
      previousInputs.selectedDate !== selectedDate ||
      previousInputs.source !== source ||
      previousInputs.countryCode !== countryCode ||
      previousInputs.timeLocale !== timeLocale
    ) {
      timeOptionsInputsRef.current = {
        venueConfig,
        selectedType,
        selectedDate,
        source,
        countryCode,
        timeLocale,
      };
      lastTimeOptionsRef.current = null;
    }

    const bookingType = findSelectedBookingType(venueConfig, selectedType);
    const bookingDetails = { type: selectedType, date: selectedDate, source };
    const options = getTimeOptions(bookingDetails, bookingType, venueConfig, countryCode, timeLocale);

    const optionsKey = JSON.stringify(options);
    if (optionsKey === lastTimeOptionsRef.current) {
      return;
    }

    lastTimeOptionsRef.current = optionsKey;
    dispatch({ type: "SET_TIME_OPTIONS", payload: options });
  }, [venueConfig, selectedType, selectedDate, source, countryCode, timeLocale, timeOptionsTick]);

  // ── Reactive computation: recompute durationOptions when time, type, or num_people changes ──
  useEffect(() => {
    if (!venueConfig || !selectedType || !selectedDate || !selectedTime) {
      dispatch({ type: "SET_DURATION_OPTIONS", payload: [] });
      return;
    }

    // Clear current duration when time genuinely changes so the auto-select effect
    // picks up the new single option. Skip the reset on the initial API population
    // (when prevTimeRef.current is still the default/initial value being set for the first time).
    if (prevTimeRef.current !== null && prevTimeRef.current !== selectedTime) {
      dispatch({ type: "UPDATE_CORE_DETAILS", payload: { duration: null } });
    }
    prevTimeRef.current = selectedTime;

    const bookingType = findSelectedBookingType(venueConfig, selectedType);
    const bookingDetails = {
      type: selectedType,
      date: selectedDate,
      time: selectedTime,
      num_people: selectedNumPeople ?? undefined,
      source,
    };
    const options = getDurationOptions(bookingDetails, bookingType, venueConfig, countryCode, timeLocale);

    dispatch({ type: "SET_DURATION_OPTIONS", payload: options });
  }, [venueConfig, selectedType, selectedDate, selectedTime, selectedNumPeople, source, countryCode, timeLocale]);

  /**
   * Auto-select duration when only one option exists.
   *
   * Mirrors `$scope.setDefaultDuration` from dmn bookController.js lines 1401-1411:
   * - If a time is selected and there is exactly 1 duration option, auto-select it.
   * - If no time is selected, set duration to null.
   */
  useEffect(() => {
    if (selectedTime && durationOptions.length === 1) {
      dispatch({
        type: "UPDATE_CORE_DETAILS",
        payload: { duration: durationOptions[0].value },
      });
    } else if (!selectedTime) {
      dispatch({
        type: "UPDATE_CORE_DETAILS",
        payload: { duration: null },
      });
    }
  }, [selectedTime, durationOptions]);

  // The invariant identifiers every /createBooking update call carries. Memoised
  // so the callbacks below keep a stable identity.
  const pendingBookingContext = useMemo<PendingBookingContext>(
    () => ({
      venueId,
      source,
      pendingBookingId: state.pendingBookingId,
      affiliateId,
      offer,
      locale,
      additionalTypes,
      venueGroup,
    }),
    [venueId, source, state.pendingBookingId, affiliateId, offer, locale, additionalTypes, venueGroup],
  );

  // ── updateBooking: call /createBooking with pending_booking_id + details ──
  const updateBooking = useCallback(
    async (details: Partial<CoreBookingDetails>, offerOverride?: string) => {
      dispatch({ type: "UPDATE_CORE_DETAILS", payload: details });
      dispatch({ type: "SET_LOADING", payload: true });

      // Claim this request's generation. Anything that supersedes it — the guest
      // navigating away, or a later update of their own — moves the counter on,
      // so this tells us whether the response still belongs to the step the
      // guest is on.
      const generation = ++updateGenerationRef.current;
      const isCurrent = () => updateGenerationRef.current === generation;
      activeRequestsRef.current += 1;

      try {
        const requestData = buildUpdateBookingRequest(
          pendingBookingContext,
          { ...state.coreBookingDetails, ...details },
          offerOverride,
        );

        const data = await call("/createBooking", requestData);
        // A current response is by definition the newest, so it settles loading
        // itself; a superseded one leaves that to the block below.
        dispatch({ type: isCurrent() ? "SET_BOOKING_DATA" : "SUPERSEDED_BOOKING_DATA", payload: data });
      } catch (err) {
        // A superseded request's failure belongs to a step the guest has left,
        // so it must not raise a banner (nor a retry) there.
        if (!isCurrent()) return;
        if (isAbortedRequest(err)) {
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }
        const message = err instanceof Error ? err.message : "An unknown error has occurred";
        dispatch({ type: "SET_ERROR", payload: message });
      } finally {
        activeRequestsRef.current -= 1;
        // `loading` covers every booking request, so a superseded one may only
        // settle it once nothing else is running. Clearing it while a newer
        // request is still in flight would lift the form's overlay and let
        // loading-driven effects resolve against state that is about to change.
        if (!isCurrent() && activeRequestsRef.current === 0) {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }
    },
    [call, pendingBookingContext, state.coreBookingDetails],
  );

  // ── persistOfferToPendingBooking: store the guest's offer choice on the pending booking ──
  //
  // The offer only reaches Collins via `/submitBooking`, which never runs when a
  // Stripe Connect deposit or paid pre-order sends the guest through a payment
  // redirect: the booking is then created server-side from the pending booking
  // by the Stripe webhook (collins-api `UpdateBooking::updateBook`), and any
  // offer that lived only in browser state is lost. Writing it to the pending
  // booking first makes it survive every path, because both create-from-pending
  // implementations copy the pending booking wholesale.
  //
  // Passing `null` (the "No thanks" choice) sends an empty offer, which clears
  // an offer previously stored from the `?offer=` URL param.
  //
  // Unlike `updateBooking` this deliberately dispatches nothing: it runs as the
  // guest moves into the pre-order/payment stages, where resetting the wizard's
  // loading flag and booking data would disrupt the stage being entered. Errors
  // are re-thrown for the caller to handle.
  const persistOfferToPendingBooking = useCallback(
    async (offerId: string | null) => {
      if (!pendingBookingContext.pendingBookingId) return;

      await call(
        "/createBooking",
        buildUpdateBookingRequest(pendingBookingContext, state.coreBookingDetails, offerId ?? ""),
      );
    },
    [call, pendingBookingContext, state.coreBookingDetails],
  );

  // ── retryCreateBooking: re-attempt fetching pending_booking_id + /createBooking after a failure ──
  const retryCreateBooking = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    activeRequestsRef.current += 1;

    try {
      await fetchPendingAndCreateBooking(
        { venueId, source, affiliateId, offer, locale, additionalTypes, venueGroup },
        dispatch,
        undefined,
        {
          type: initialType,
          num_people: initialNumPeople,
          date: initialDate,
          time: initialTime,
          duration: initialDuration,
        },
        call,
      );
    } catch (err) {
      if (!isAbortedRequest(err)) {
        const message = err instanceof Error ? err.message : "An unknown error has occurred";
        dispatch({ type: "SET_ERROR", payload: message });
      }
    } finally {
      activeRequestsRef.current -= 1;
    }
  }, [
    call,
    venueId,
    source,
    affiliateId,
    offer,
    locale,
    additionalTypes,
    venueGroup,
    initialType,
    initialNumPeople,
    initialDate,
    initialTime,
    initialDuration,
  ]);

  // ── submitBooking: call /submitBooking with full details ──
  // Re-throws on failure so callers can decide whether to advance the stage.
  const submitBooking = useCallback(
    async (fullDetails: BookingSubmitRequest) => {
      dispatch({ type: "SET_SUBMITTING", payload: true });

      try {
        const data = await call("/submitBooking", fullDetails);
        dispatch({ type: "SET_SUBMIT_RESULT", payload: data });
      } catch (err) {
        if (isUnconfirmedOutcome(err)) {
          dispatch({ type: "SET_SUBMIT_UNCONFIRMED" });
        } else {
          const message = err instanceof Error ? err.message : "An unknown error has occurred";
          dispatch({ type: "SET_SUBMIT_ERROR", payload: message });
        }
        throw err;
      }
    },
    [call],
  );

  // ── clearSubmitError: allow the form to dismiss submit errors ──
  const clearSubmitError = useCallback(() => {
    dispatch({ type: "CLEAR_SUBMIT_ERROR" });
  }, []);

  /**
   * Discard the validation errors of the step the guest has just left.
   *
   * Every navigation away from a step that can own errors calls this — a stage
   * change, a browser back/forward, or a widget wizard sub-step transition — so
   * it is also where an in-flight `/createBooking` update is superseded. Without
   * that, a response landing after the guest has moved would copy the warning
   * straight back into state and show it on the step they are now on.
   */
  const clearValidationErrors = useCallback(() => {
    updateGenerationRef.current += 1;
    dispatch({ type: "CLEAR_VALIDATION_ERRORS" });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: "CLEAR_ERROR" });
  }, []);

  // ── updateCoreDetails: update local state without API call (for reactive recomputation) ──
  const updateCoreDetails = useCallback((details: Partial<CoreBookingDetails>) => {
    dispatch({ type: "UPDATE_CORE_DETAILS", payload: details });
  }, []);

  // ── setStage: explicit stage transitions ──
  const setStage = useCallback((stage: BookingWizardState["stage"]) => {
    dispatch({ type: "SET_STAGE", payload: stage });
  }, []);

  const setIsEnquiry = useCallback((isEnquiry: boolean) => {
    dispatch({ type: "SET_IS_ENQUIRY", payload: isEnquiry });
  }, []);

  const setPaymentUnconfirmed = useCallback(() => {
    dispatch({ type: "SET_PAYMENT_UNCONFIRMED" });
  }, []);

  const clearPendingBookingUser = useCallback(() => {
    dispatch({ type: "CLEAR_PENDING_BOOKING_USER" });
  }, []);

  const resetForVenueChange = useCallback(() => {
    // The wizard is starting again on another venue, so an update still in
    // flight for the previous one must not repopulate the state being cleared.
    updateGenerationRef.current += 1;
    dispatch({ type: "RESET_FOR_VENUE_CHANGE" });
  }, []);

  return {
    state,
    updateBooking,
    persistOfferToPendingBooking,
    updateCoreDetails,
    submitBooking,
    retryCreateBooking,
    clearSubmitError,
    clearValidationErrors,
    clearError,
    setStage,
    setIsEnquiry,
    setPaymentUnconfirmed,
    clearPendingBookingUser,
    resetForVenueChange,
  };
}
